<script setup lang="ts">
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import Icon from "@/core/components/icon/Icon.vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/core/components/ui/dialog";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import {computed, ComputedRef, reactive, watch} from "vue";
import {useExtensionStore} from "@/stores/extension.store";
import Extension from "@/core/interfaces/extension/Extension";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {customerProgramApi} from "@/container";
import {Program, ProgramInclusionGroup} from "@/program/interfaces/Program";
import ProgramLogo from "@/program/components/program-logo/ProgramLogo.vue";
import {Input} from "@/core/components/ui/input";
import {AxiosError} from "axios";
import {usePatientStore} from "@/stores/patient.store";
import {useRoute, useRouter} from "vue-router";
import {useToastStore} from "@/stores/toast.store";
import Add from "@/core/components/icons/Add.vue";

const route = useRoute();
const router = useRouter();

const emit = defineEmits(['program-added']);
const {showSuccess, showError} = useToastStore();

const props = defineProps<{
    consumer: Beneficiary | Customer,
    autoOpen?: boolean,
}>();

const state = reactive({
    selectedExtensionId: null as string | null,
    selectedProgramId: null as string | null,
    selectedGroupSlug: null as string | null,
    comments: '' as string,
    open: false,
    loading: false,
    groups: [] as ProgramInclusionGroup[],
    error: '' as string,
});

const extensionStore = useExtensionStore();
const patientStore = usePatientStore();
const api = customerProgramApi();

/**
 * Open the dialog when requested for this consumer. One dialog is mounted per
 * household member: without the key match, all of them would open stacked and
 * the inclusion would land on whichever sits on top. The request is consumed
 * so that a later one for the same consumer opens the dialog again.
 */
watch(() => route.query, () => {
    if (route.query.addProgramDialog !== props.consumer.consumer_key) {
        return;
    }

    state.open = true;
    router.replace({query: {...route.query, addProgramDialog: undefined}});
}, {immediate: true});

/**
 * The program extension selected by the user.
 */
const selectedExtension: ComputedRef<Extension | undefined> = computed(() => {
    return extensionStore.enabledProgramExtensions.find((extension: Extension) => {
        return extension.id.toString() == state.selectedExtensionId;
    });
});

/**
 * The program extension selected by the user.
 */
const selectedProgram: ComputedRef<Program | undefined> = computed(() => {
    return selectedExtension.value?.program_bundle?.programs.find((program: Program) => {
        return program.id.toString() == state.selectedProgramId;
    });
});

/**
 * Get the programs to display in the select box.
 */
const selectableExtensions = computed(() => {
    const options = extensionStore.enabledProgramExtensions
        .filter((extension) => extension.organization.entity_type !== 'hospital')
        .map((extension) => ({
            label: extension.name,
            value: extension.id.toString(),
        }));

    if (options.length === 0) {
        return [{
            label: 'Aucun programme disponible',
            value: '0',
            disabled: true,
        }];
    }

    return options;
});

/**
 * Get the programs to display in the select box.
 */
const selectablePrograms: ComputedRef<{ label: string, value: string }[] | undefined> = computed(() => {
    return selectedExtension.value?.program_bundle!.programs
        .map((program) => ({
            label: program.name,
            value: program.id.toString(),
        }));
});

/**
 * Get the selectable program groups to display in the select box.
 */
const selectableGroups: ComputedRef<{ label: string, value: string }[] | undefined> = computed(() => {
    const groups = state.groups
        .map((group: ProgramInclusionGroup) => ({
            label: group.label,
            value: group.slug,
        }));

    groups.unshift({
        label: 'Ciblage générique',
        value: '0',
    });

    return groups;
});

/**
 * Include the consumer in the selected program via the API.
 */
const include = async () => {
    state.loading = true;

    try {
        await api.include(
            selectedProgram.value!.id,
            props.consumer.consumer_key,
            state.selectedGroupSlug == '0' ? null : state.selectedGroupSlug,
            state.comments
        );

        emit('program-added', selectedProgram.value!.id);

        await patientStore.refresh();

        state.selectedExtensionId = null;
        state.selectedProgramId = null;
        state.selectedGroupSlug = null;
        state.comments = '';

        showSuccess({
            content: `Le patient a bien été ajouté au programme ${selectedProgram.value?.name}.`,
        });

        state.open = false;
    } catch (error) {
        const code = (error as AxiosError).response?.status;

        if (code == 409) {
            state.error = 'Le patient est déjà inclus dans ce ciblage.';
        } else {
            showError({
                content: "Une erreur est survenue lors de l'ajout du patient au programme.",
            });
        }
    } finally {
        state.loading = false;
    }
};

/**
 * Watch for the selected extension and set selectable programs.
 */
watch(selectablePrograms, (programs) => {
    state.error = '';
    state.groups = [];
    state.selectedGroupSlug = null;

    if (programs && programs.length > 0) {
        state.selectedProgramId = programs[0].value;
    }
});

/**
 * Watch for the selected program and fetch the inclusion groups.
 */
watch(selectedProgram, async (program) => {
    state.error = '';

    if (program) {
        state.loading = true;
        state.groups = await api.groups(program.id);
        state.loading = false;
    }
});

/**
 * Watch for the selected group and reset the error message.
 */
watch(() => state.selectedGroupSlug, async () => {
    state.error = '';
});

</script>

<template>
  <Dialog :open="state.open" @update:open="state.open = $event">
    <DialogTrigger>
      <BaseButton density="compact" class="!shadow-sm px-3">
        <Add :size="14" class="mr-1" />
        <span class="text-xs">Ajouter</span>
      </BaseButton>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          Ajouter à un programme
        </DialogTitle>
        <DialogDescription>
          A quel programme souhaitez-vous ajouter le patient ?
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2">
        <BaseSelect
          v-model:model-value="state.selectedExtensionId"
          placeholder="Sélectionnez un programme"
          :options="selectableExtensions"
        />
        <BaseSelect
          v-if="selectablePrograms && selectablePrograms.length > 1"
          v-model:model-value="state.selectedProgramId"
          placeholder="Sélectionnez un sous-programme"
          :options="selectablePrograms"
        />
        <div v-if="selectedProgram && selectableGroups && state.groups.length > 0" class="flex gap-6 items-center my-2">
          <ProgramLogo :program="selectedProgram" class="border rounded-lg shadow" />
          <div class="flex-1 flex flex-col gap-2">
            <BaseSelect
              v-model:model-value="state.selectedGroupSlug"
              placeholder="Sélectionnez un ciblage"
              :options="selectableGroups"
            />
            <Input v-model="state.comments" placeholder="Commentaire ..." />
          </div>
        </div>
        <div class="flex justify-end items-center mt-4">
          <div v-if="state.error" class="text-red-500 text-sm mr-2">
            {{ state.error }}
          </div>
          <BaseButton primary :disabled="!state.selectedGroupSlug" :loading="state.loading" @click="include">
            <Icon name="mdi-plus-box-multiple" class="mr-1 text-white" />
            Ajouter {{ consumer.information.first_name }}
          </BaseButton>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
